import { type Stripe } from "stripe";

import { type Database } from "@fitness-app/supabase";

import { type DiscordAutomationTask } from "./Automation";
import { type ClientInvoiceData, type WarmupPeriod } from "./Order";
import {
  type Currency,
  type EndAccessStrategy,
  type InfiniteProductDuration,
  type LastsForProductDuration,
  type OrderPaymentType,
  type OrderPaymentTypeOption,
  type PriceAccessPeriod,
  type ProductTypeEnum,
  type RecurringInterval,
  type StripePrice,
  type UntilDateProductDuration,
} from "./Product";
import { type TraineeStatus } from "./Trainee";

export type ProductClientEntity = Database["public"]["Tables"]["product_client"]["Row"];

export interface Coupon {
  code: string;
}

export type SubscriptionStatus =
  | "active"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "past_due"
  | "trialing"
  | "unpaid"
  | "imported"
  | "paused";

export type OneTimePaymentStatus = "active" | "past_due" | "imported" | "expired";

export type SubscriptionInterval = "day" | "month" | "week" | "year";

export interface Recurring {
  interval_count: number;
  trial_period_days?: number | null;
  interval: SubscriptionInterval;
  endAccessStrategy?: EndAccessStrategy;
}

export type AccessPeriodType = "recurring" | "one_time";

export type ExtensionType = "freePeriodAfterEnd" | "pauseCollection";

interface PauseCollection {
  behavior: "keep_as_draft" | "mark_uncollectible" | "void";
  resumes_at: number | null;
  original_access_date: number;
  reason?: "automated_product_access_reschedule" | "manual_pause";
}

export type PaidInstallmentsStatus = "paid";

export interface SubscriptionProductAccessPeriod {
  cycleNumber: number;
  startAt: number;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  type: "recurring";
  productId: string;
  productName: string;
  subscriptionId: string;
  status: SubscriptionStatus | PaidInstallmentsStatus;
  recurring: Recurring;
  cancelAtPeriodEnd: boolean;
  pauseCollection?: null | PauseCollection;
  endDate?: number | null;
  installmentEndsAt?: number | null;
  installments?: number | null;
  connectedStripeAccountId?: string;
  trial?: null | { startAt: number; endAt: number };
  warmupPeriod?: WarmupPeriod | null;
  customDuration?: {
    count: number;
    unit: RecurringInterval;
    addedDays?: number[];
  } | null;
  isManualPayment?: boolean;
}

export interface OneTimeProductAccessPeriod {
  currentPeriodStart: number;
  currentPeriodEnd: number;
  type: "one_time";
  productId: string;
  productName: string;
  recurring: Recurring | null;
  endAccessStrategy?: EndAccessStrategy;
  status?: OneTimePaymentStatus;
  pauseCollection?: null | PauseCollection;
  connectedStripeAccountId?: string;
  trial?: never;
  warmupPeriod?: never;
}

export interface NoneLimitedAccessPeriod {
  currentPeriodStart: number;
  currentPeriodEnd?: never;
  recurring?: never;
  endAccessStrategy: EndAccessStrategy;
  productId: string;
  productName: string;
  type: "none_limited";
  pauseCollection?: never;
  connectedStripeAccountId?: string;
  trial?: never;
  warmupPeriod?: never;
}

export type PaidLastOrder = {
  amount: number;
  currency: Currency;
  priceId: string;
  orderId: string;
  free: false;
  paymentGatewayInvoiceId?: string | null;
  customInvoiceName?: string;
};

export type FreeLastOrder = {
  orderId: string;
  free: true;
  priceId: null;
  amount?: never;
  current?: never;
};

export type NFTLastOrder = {
  orderId: string;
  free: true;
  priceId: string;
  amount?: never;
  current?: never;
};

export type LastOrder = PaidLastOrder | FreeLastOrder | NFTLastOrder;

export interface ProductClientAuthor {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  stripeAccountId?: string | null;
  creator?: string;
}

export interface RenewalAccessPeriodReminder {
  eventId: string;
  timestamp: number;
  notificationId?: string | null;
  emailSentTimestamp?: number;
}

export interface DefaultReminderForYear {
  1: null | RenewalAccessPeriodReminder;
  3: null | RenewalAccessPeriodReminder;
  7: null | RenewalAccessPeriodReminder;
  14: null | RenewalAccessPeriodReminder;
}

export interface DefaultReminderForMonths {
  1: null | RenewalAccessPeriodReminder;
  3: null | RenewalAccessPeriodReminder;
  7: null | RenewalAccessPeriodReminder;
  14?: never;
}

export type RenewalAccessPeriodReminders = DefaultReminderForYear | DefaultReminderForMonths;

export enum RenewalAccessPeriodStatus {
  InProgress = "inProgress",
  Finished = "finished",
}

export interface RenewalAccessPeriod {
  strategy: EndAccessStrategy;
  checkoutLink: string;
  reminders: RenewalAccessPeriodReminders;
  status: RenewalAccessPeriodStatus;
}

export enum ClientArchiveReason {
  AccessEnd = "access_end",
  Refund = "refund",
  Unsubscribe = "unsubscribe",
  NFTSell = "nft_sell",
}

/**
 * Free-form audit note on a product client (TRE-1511). System flows (payoff,
 * migrations) append notes so the admin timeline explains non-obvious state —
 * e.g. why a plan shows "paid" with a canceled subscription.
 */
export interface ProductClientNote {
  /** Stable id (system notes reuse the source operation id → idempotent append). */
  id: string;
  createdAt: string;
  /** Set when the note content was edited after creation. */
  updatedAt?: string | null;
  /** "system" or the authoring user id. */
  author: string;
  /** Machine-readable origin, e.g. "payoff". */
  kind?: string | null;
  content: string;
}

/**
 * Payoff offer granted to ONE client from the admin (TRE-1713).
 *
 * The price-level `payoffUpsell` config covers every future sale on that price
 * inside a window counted from the purchase date. This grant is the manual
 * counterpart: it names the client, carries its own discount and an ABSOLUTE
 * expiry, and wins over the price config in the backend eligibility resolver.
 * It never relaxes the plan guards (±48h billing window, at least two
 * remaining installments, no manual installments, PAYOFF_ENABLED).
 */
/**
 * What the client gets for paying the installments off early (TRE-1721).
 *
 * `discount` is the original mechanic: a percentage off the remaining balance.
 * It cannot express "one installment free" for a batch — the same percentage is
 * a different amount for everyone, depending on how many installments are left,
 * so an email promising a named value would be wrong for most recipients.
 *
 * `lastInstallmentFree` prices that promise directly: the payoff costs the
 * remaining balance MINUS the last installment, computed per client from the
 * live schedule. The percentage becomes an outcome, not an input.
 *
 * `bonusTime` (TRE-1725) moves the reward off the price entirely: the client
 * pays the FULL remaining balance (`discountPct` 0, no waived installment) and
 * gets `bonusDays` of extra access instead. It is the only reward that changes
 * the access horizon, which is why the number lives on the offer and is frozen
 * into the operation snapshot — the email must promise exactly what the
 * finalizer grants.
 */
export type PayoffOfferReward =
  | { type: "discount"; discountPct: number }
  | { type: "lastInstallmentFree" }
  | { type: "bonusTime"; bonusDays: number };

export interface ProductClientPayoffOffer {
  enabled: boolean;
  /**
   * Payoff discount in percentage points — overrides the price-level value.
   *
   * Legacy field, kept because grants written before TRE-1721 carry only this.
   * `reward` wins when present, and for anything other than a discount this is
   * written as **0** — the real percentage is per client and only known when
   * the payoff is priced, so there is no meaningful value to store here.
   * Read `reward` (or `PayoffQuote.discountPct`) instead of this field.
   */
  discountPct: number;
  /** Reward mechanic; absent = the legacy `discountPct` discount. */
  reward?: PayoffOfferReward | null;
  /** Unix ms — end of the offer. Absolute, NOT windowDays from `createdAt`. */
  expiresAt: number;
  grantedAt: number;
  /** User id of the staff member who granted the offer. */
  grantedBy: string;
  source: "admin_single" | "admin_bulk";
  /** Shared id of one bulk action — audit trail and conversion stats. */
  batchId?: string | null;
  /** Unix ms of the notification email; absent when the grant was silent. */
  emailSentAt?: number | null;
  /** Reminder for clients who did not take the offer (TRE-1721). */
  followUp?: PayoffOfferFollowUp | null;
  revokedAt?: number | null;
  revokedBy?: string | null;
  /**
   * Unix ms the client actually used the offer — written by the payoff
   * finalization (TRE-1737).
   *
   * A used offer is not an active one: without this the admin kept showing the
   * gold "offer until …" tag and its actions (mint a link, change, revoke) on a
   * plan that is already paid off, and the client list kept the same tag.
   */
  redeemedAt?: number | null;
}

/** One reminder for a granted offer nobody used yet (TRE-1721). */
export interface PayoffOfferFollowUp {
  enabled: boolean;
  /** Days after the grant. */
  afterDays: number;
  /** Unix ms the reminder is due — grant time + `afterDays`. */
  scheduledAt: number;
  /** Unix ms of the delivered reminder; null until it goes out. */
  sentAt?: number | null;
  /**
   * Why the reminder never went out — decided at send time, not at planning
   * time: `converted` | `expired` | `revoked` | `not_eligible` | `disabled`.
   */
  skippedReason?: string | null;
}

export interface ArchivedInfo {
  archivedAt: number;
  by: string | "system";
  reason: ClientArchiveReason;
}

/**
 * Dostęp powiązany (TRE-2110): dodatek nadany "jak w produkcie X" pamięta, za
 * którym dostępem podąża. `accessPeriod` dodatku zostaje zwykłym `one_time`
 * (mobile i client-web znają tylko `one_time`/`recurring`), a handler eventów
 * źródła (przedłużenie / archiwizacja) przepisuje koniec dostępu tylko wtedy,
 * gdy `followSource` jest włączone — przedłużenie pakietu nie zawsze obejmuje
 * dodatek, więc to wybór przy nadawaniu.
 */
/**
 * Etap sprawy windykacyjnej (TRE-2168) — odwzorowanie procesu, który do tej pory
 * żył w arkuszu: od pierwszego przypomnienia po przekazanie do firmy windykacyjnej.
 * Etapy „zamykające" (recovered / written_off / resignation) zostają na rekordzie
 * jako historia, dopóki nowa zaległość nie otworzy sprawy od nowa.
 */
export const CollectionsStage = {
  /** Zaległość widoczna, nikt jeszcze nic nie zrobił. */
  New: "new",
  /** Przypomnienie (mail / SMS / telefon) poszło. */
  Reminder: "reminder",
  /** Formalne wezwanie do płatności. */
  Demand: "demand",
  /** Ostateczne wezwanie przed windykacją zewnętrzną. */
  FinalDemand: "final_demand",
  /** Sprawa przekazana do firmy windykacyjnej (Vindicat). */
  ExternalCollection: "external_collection",
  /** Klientka w kontakcie, uzgodniony indywidualny harmonogram — eskalacja wstrzymana. */
  Arrangement: "arrangement",
  /** Zaległość spłacona. */
  Recovered: "recovered",
  /** Dług umorzony decyzją firmy. */
  WrittenOff: "written_off",
  /** Rezygnacja przyjęta — rata nie jest już należna. */
  Resignation: "resignation",
} as const;

export type CollectionsStage = (typeof CollectionsStage)[keyof typeof CollectionsStage];

/** Etapy, przy których automatyczna drabinka przypomnień ma milczeć. */
export const COLLECTIONS_STAGES_ON_HOLD: readonly CollectionsStage[] = [
  CollectionsStage.Arrangement,
  CollectionsStage.Recovered,
  CollectionsStage.WrittenOff,
  CollectionsStage.Resignation,
];

/** Polskie etykiety etapów — panel i treść notatek systemowych mówią jednym językiem. */
export const COLLECTIONS_STAGE_LABELS: Record<CollectionsStage, string> = {
  new: "Nowa zaległość",
  reminder: "Przypomnienie wysłane",
  demand: "Wezwanie do zapłaty",
  final_demand: "Ostateczne wezwanie",
  external_collection: "Windykacja zewnętrzna",
  arrangement: "Ustalenia indywidualne",
  recovered: "Odzyskane",
  written_off: "Umorzone",
  resignation: "Rezygnacja przyjęta",
};

export interface ProductClientCollections {
  stage: CollectionsStage;
  /** ISO — kiedy ktoś ma znów zadzwonić / napisać; null = bez terminu. */
  nextContactAt?: string | null;
  updatedAt: string;
  /** Id użytkownika panelu, który ustawił etap. */
  updatedBy: string;
}

export interface LinkedAccess {
  sourceProductClientId: string;
  sourceProductId: string;
  sourceProductName: string;
  followSource: boolean;
  linkedAt: number;
}

export type ProductClientDuration =
  | null
  | (UntilDateProductDuration & { startAt: number; endAt: number })
  | (InfiniteProductDuration & { startAt?: never; endAt?: never })
  | (LastsForProductDuration & { startAt: number; endAt: number });

export interface BaseProductClientDocument extends Omit<
  ProductClientEntity,
  | "archivedInfo"
  | "accessPeriod"
  | "accessPeriodRenewal"
  | "scheduledTasks"
  | "upsellToProduct"
  | "author"
  | "client"
  | "downgradedToFromNextPeriod"
  | "productDuration"
  | "coupon"
  | "paymentMethod"
  | "scheduledAccessPeriod"
  | "notes"
  | "payoffOffer"
  | "linkedAccess"
> {
  status: "active" | "archived";
  archivedInfo?: ArchivedInfo | null;
  /** Za czyim dostępem podąża ten dostęp (TRE-2110); null dla zwykłych klientek. */
  linkedAccess?: LinkedAccess | null;
  productType: ProductTypeEnum;
  updatedAt: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  coupon: Coupon | null;
  refCode: string | null;
  paymentType: OrderPaymentType;
  productId: string;
  author: ProductClientAuthor;
  paymentGatewayAuthorId: string | null;
  connectedOrders: string[];
  product: {
    name: string;
    description: string;
    images: string[];
  };
  client: ClientInvoiceData;
  imported: boolean | null;
  importId: string | null;
  invoiceRequested: boolean | null;
  scheduledTasks?: DiscordAutomationTask[] | null;
  discordId: string | null;
  previousEmails: string[];
  /** Audit notes shown in the admin client details (system + manual). */
  notes?: ProductClientNote[] | null;
  /** Payoff offer granted individually from the admin (TRE-1713). */
  payoffOffer?: ProductClientPayoffOffer | null;
  /** Etap sprawy windykacyjnej (TRE-2168); null = brak otwartej sprawy. */
  collections?: ProductClientCollections | null;
  productDuration: ProductClientDuration;
  upsellToProduct?: null | {
    productId: string;
    productName: string;
    date: number;
    type: ProductTypeEnum;
  };
  downgradedToFromNextPeriod: null | {
    priceId: string;
    amount: number;
    currency: string;
  };
  userId: string | null;
  customStripeAccountId: string | null;
}

// export type ScheduledAccess =
//   | SubscriptionProductAccessPeriod
//   | OneTimeProductAccessPeriod
//   | Stripe.SubscriptionSchedule.Phase;

export type ScheduledOneTimeAccessPeriod = {
  start_date: number;
  end_date: number;
  priceId: string;
  type: "one_time";
  orderId: string;
  connectedPrice: StripePrice;
  accessPeriod: PriceAccessPeriod;
  metadata: Record<string, string | number | null>;
  isOverlappingSubscription?: never;
};

export type FreeExtensionAccessPeriod = {
  start_date: number;
  end_date: number;
  priceId: null;
  type: "free_extension";
  orderId: null;
  connectedPrice: null;
  accessPeriod: null;
  metadata: Record<string, string | number | null>;
  isOverlappingSubscription?: never;
};

export type ScheduledOverlappingAccessPeriod = SubscriptionProductAccessPeriod & {
  isOverlappingSubscription: true;
  metadata: Record<string, string | number | null>;
  start_date: number;
  end_date: number;
  priceId: string;
  type: "recurring";
  orderId: string;
};

export type ScheduledAccess =
  | (Stripe.SubscriptionSchedule.Phase & {
      type?: never;
      isOverlappingSubscription?: never;
    })
  | ScheduledOneTimeAccessPeriod
  | ScheduledOverlappingAccessPeriod
  | FreeExtensionAccessPeriod;

export interface SubscriptionProductClient extends BaseProductClientDocument {
  paymentType: typeof OrderPaymentTypeOption.Recurring;
  accessPeriod: SubscriptionProductAccessPeriod;
  scheduledAccessPeriod?: ScheduledAccess[] | null;
  paymentMethod: Stripe.PaymentMethod.Card | null;
  paymentGatewayCustomerId: string;
  invoiceRequested: boolean | null;
  accessPeriodRenewal?: RenewalAccessPeriod | null;
  lastOrder: string;
  downgradedToFromNextPeriod: null | {
    priceId: string;
    amount: number;
    currency: string;
  };
}

export interface OneTimeProductClient extends BaseProductClientDocument {
  paymentType: typeof OrderPaymentTypeOption.OneTime;
  paymentGatewayCustomerId: string;
  scheduledAccessPeriod?: ScheduledAccess[] | null;
  accessPeriod?: OneTimeProductAccessPeriod | NoneLimitedAccessPeriod | null;
  invoiceRequested: boolean | null;
  accessPeriodRenewal?: RenewalAccessPeriod | null;
  lastOrder: string;
}

export interface FreeProductClient extends BaseProductClientDocument {
  paymentType: typeof OrderPaymentTypeOption.Free;
  coupon: null;
  accessPeriod: null;
  invoiceRequested: null;
  lastOrder: string;
}

export type ProductClient = SubscriptionProductClient | OneTimeProductClient | FreeProductClient;

export type ProductClientWithTraineeInfo = ProductClient & {
  trainee: { status: TraineeStatus | null } | null;
  profile: {
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
  } | null;
};
